import { EditorView } from '@tiptap/pm/view'
import ReactDOM from 'react-dom'

import addWidgetPlugin, { findWidget } from './add-widget-plugin'

import { isVideo } from '~lib/utils/files'

/* eslint-disable unicorn/consistent-destructuring, promise/always-return, promise/catch-or-return */

export type UploadPlaceholderComponentProps = {
  file: File
}

export type MediaUploadOptions = {
  uploadFile: (file: File) => Promise<string>
  onSuccess?: () => void
  onError?: () => void
  PlaceholderComponent?: (props: UploadPlaceholderComponentProps) => JSX.Element
}

export default function insertMedia(view: EditorView, pos: number, files: File[], options: MediaUploadOptions): void {
  const { uploadFile, onSuccess, onError } = options

  const { schema } = view.state

  // track how many images have succeeded or failed
  let complete = 0

  files.forEach((file) => {
    // Use an object to act as the ID for this upload, clever.
    const id = {}

    const { tr } = view.state

    // insert a placeholder at this position
    const widget = document.createElement('div')
    if (options.PlaceholderComponent) {
      const Component = options.PlaceholderComponent
      ReactDOM.render(<Component file={file} />, widget)
    } else {
      const img = document.createElement('img')
      img.src = URL.createObjectURL(file)
      widget.append(img)
    }

    tr.setMeta(addWidgetPlugin, {
      add: { id, widget, pos },
    })

    view.dispatch(tr)

    uploadFile(file)
      .then((src) => {
        if (isVideo(file)) {
          const newVideo = document.createElement('video')
          newVideo.addEventListener('error', (error) => {
            throw error
          })
          newVideo.addEventListener('loadstart', () => {
            const placeholderPosition = findWidget(view.state, id)

            // if the placeholder has been deleted then forget about inserting this image
            if (placeholderPosition === null) return

            const transaction = view.state.tr
              .replaceWith(
                placeholderPosition,
                placeholderPosition,
                schema.nodes.video.create({ src, filename: file.name })
              )
              .setMeta(addWidgetPlugin, { remove: { id } })
            view.dispatch(transaction)
          })

          newVideo.src = src
          newVideo.load()
          return
        }

        // otherwise, insert it at the placeholder's position, and remove the placeholder itself
        const newImg = new Image()

        newImg.addEventListener('load', () => {
          const placeholderPosition = findWidget(view.state, id)

          // if the placeholder has been deleted then forget about inserting this image
          if (placeholderPosition === null) return

          const transaction = view.state.tr
            .replaceWith(
              placeholderPosition,
              placeholderPosition,
              schema.nodes.image.create({ src, filename: file.name })
            )
            .setMeta(addWidgetPlugin, { remove: { id } })

          view.dispatch(transaction)
        })

        newImg.addEventListener('error', (error) => {
          throw error
        })

        newImg.src = src
      })
      .catch((error) => {
        console.error(error)

        // cleanup the placeholder if there is a failure
        const transaction = view.state.tr.setMeta(addWidgetPlugin, {
          remove: { id },
        })

        view.dispatch(transaction)

        if (onError) {
          onError()
        }
      })
      .finally(() => {
        complete += 1
        if (complete === files.length && onSuccess) onSuccess()
      })
  })
}
